import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchSpaceConflicts,
  fetchSpaceConflictById,
  resolveSpaceConflict,
} from '@/lib/api/space-conflicts'
import type { SpaceConflictFilters, SpaceConflictUpdate } from '@/types/space-conflicts'
import { toast } from 'sonner'

/**
 * Query key factory for space conflicts
 * Provides consistent keys for cache management
 */
export const spaceConflictKeys = {
  all: ['space-conflicts'] as const,
  lists: () => [...spaceConflictKeys.all, 'list'] as const,
  list: (organizationId: string, filters?: SpaceConflictFilters) =>
    [...spaceConflictKeys.lists(), organizationId, filters] as const,
  details: () => [...spaceConflictKeys.all, 'detail'] as const,
  detail: (id: string) => [...spaceConflictKeys.details(), id] as const,
}

/**
 * Fetch all space conflicts for current organization with optional filtering
 */
export function useSpaceConflicts(filters?: SpaceConflictFilters) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: spaceConflictKeys.list(organizationId || '', filters),
    queryFn: () => fetchSpaceConflicts(organizationId!, filters),
    enabled: !!organizationId,
  })
}

/**
 * Fetch a single space conflict by ID
 */
export function useSpaceConflictById(id: string | undefined) {
  return useQuery({
    queryKey: spaceConflictKeys.detail(id || ''),
    queryFn: () => fetchSpaceConflictById(id!),
    enabled: !!id,
  })
}

/**
 * Resolve a space conflict with resolution details
 */
export function useResolveSpaceConflict() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: SpaceConflictUpdate
    }) => {
      return resolveSpaceConflict(id, data)
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: spaceConflictKeys.lists() })
      queryClient.invalidateQueries({ queryKey: spaceConflictKeys.detail(id) })
      toast.success('Space conflict resolved successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to resolve space conflict', {
        description: error.message,
      })
    },
  })
}
